import React, { useEffect, useState } from "react";
import { FaBuilding, FaSearch, FaExclamationTriangle, FaBan, FaCheck } from "react-icons/fa";
import Drawer from "../../shared/Drawer";
import { useDebounce } from "../../../Hooks/useDebounce";

/**
 * Firma bağlama paneli (#326) — "Alt Firma / Alt Bayi Ekle" ve "Müşteri Ekle"
 * açılır liste + onay penceresi yerine sağdan açılan panelde: arama → seç →
 * seçilen firmanın özeti (varsa engel / uyarı) → Ekle.
 *
 * @param {string} title, subtitle, confirmText
 * @param {(q: string) => Promise<Array>} onSearch      en az 2 karakterde çağrılır
 * @param {(firm) => Promise<{block?: string, warning?: string, detail?: string}>} [onInspect]
 *        seçilen firmayı denetler (ör. başka bayiye bağlı mı); `block` varsa Ekle kapalı
 * @param {(firm) => Promise} onConfirm   başarılıysa panel kapanır
 */
export default function AddFirmDrawer({
  open,
  onClose,
  title,
  subtitle,
  confirmText = "Ekle",
  onSearch,
  onInspect,
  onConfirm,
}) {
  const [query, setQuery] = useState("");
  const debounced = useDebounce(query, 350);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Her açılışta temiz başlar
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setResults([]);
    setSelected(null);
    setInspection(null);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (!debounced || debounced.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    let alive = true;
    setSearching(true);
    Promise.resolve(onSearch(debounced.trim()))
      .then((list) => alive && setResults(Array.isArray(list) ? list : []))
      .catch(() => alive && setResults([]))
      .finally(() => alive && setSearching(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, open]);

  const choose = async (firm) => {
    setSelected(firm);
    setInspection(null);
    setError(null);
    if (!onInspect) return;
    setInspecting(true);
    try {
      setInspection((await onInspect(firm)) || null);
    } catch {
      setInspection({ warning: "Firma bilgisi alınamadı; bağlantı sunucuda yine denetlenir." });
    } finally {
      setInspecting(false);
    }
  };

  const confirm = async () => {
    if (!selected || inspection?.block) return;
    setSaving(true);
    setError(null);
    try {
      await onConfirm(selected);
      onClose?.();
    } catch (err) {
      setError(err?.data?.message || err?.message || "İşlem başarısız.");
    } finally {
      setSaving(false);
    }
  };

  const tooShort = query.trim().length < 2;

  return (
    <Drawer
      open={open}
      onClose={() => !saving && onClose?.()}
      title={title}
      subtitle={subtitle}
      titleIcon={<FaBuilding className="w-4 h-4 text-gray-600" />}
      width="sm"
    >
      <Drawer.Body className="px-5 py-4 space-y-4">
        <label className="relative block">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(null);
              setInspection(null);
            }}
            placeholder="Firma adı, bayser no veya email"
            aria-label="Firma ara"
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none"
          />
        </label>

        {selected ? (
          <div className="rounded-xl border border-gray-200 p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900 break-words">{selected.firmName}</p>
                <p className="text-xs text-gray-500">
                  {[selected.bayserNo && `Bayser: ${selected.bayserNo}`, selected.email].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelected(null);
                  setInspection(null);
                }}
                className="text-xs font-semibold text-gray-500 hover:text-gray-800 shrink-0"
              >
                Değiştir
              </button>
            </div>
            {inspecting && <p className="text-xs text-gray-400">Firma bilgisi denetleniyor…</p>}
            {inspection?.detail && <p className="text-xs text-gray-600">{inspection.detail}</p>}
            {inspection?.block && (
              <p role="alert" className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <FaBan className="w-3 h-3 mt-0.5 shrink-0" />
                {inspection.block}
              </p>
            )}
            {inspection?.warning && !inspection?.block && (
              <p className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <FaExclamationTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                {inspection.warning}
              </p>
            )}
          </div>
        ) : searching ? (
          <div className="flex justify-center py-8">
            <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tooShort ? (
          <p className="text-xs text-gray-400 text-center py-8">Aramak için en az 2 karakter girin</p>
        ) : results.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-8">Sonuç bulunamadı</p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden" aria-label="Arama sonuçları">
            {results.map((f) => (
              <li key={f._id}>
                <button
                  type="button"
                  onClick={() => choose(f)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                >
                  <FaBuilding className="w-3 h-3 text-gray-400 shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-gray-800 truncate">{f.firmName}</span>
                    {(f.bayserNo || f.email) && (
                      <span className="block text-[11px] text-gray-500 truncate">
                        {[f.bayserNo, f.email].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && (
          <p role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
      </Drawer.Body>
      <Drawer.Footer className="flex justify-end gap-2.5 px-5 py-3.5 border-t border-gray-100 bg-gray-50/50">
        <button
          type="button"
          onClick={() => onClose?.()}
          disabled={saving}
          className="px-4 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Vazgeç
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={!selected || inspecting || saving || !!inspection?.block}
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <FaCheck className="w-3 h-3" />
          )}
          {saving ? "Ekleniyor..." : confirmText}
        </button>
      </Drawer.Footer>
    </Drawer>
  );
}
